<template>
  <v-container fluid>
    <v-card elevation="2">
      <!-- ——— header ——— -->
      <v-card-title class="d-flex align-center tableheader white--text">
        <v-icon left color="white">mdi-currency-usd</v-icon>
        Currencies
        <v-spacer/>
        <v-text-field
          v-model="search"
          label="Search (currency name, code, country)"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="solo-filled"
          flat hide-details single-line
          class="me-4"
        />
        <v-btn color="primary" @click="openDialog(null)" v-if="authStore.canEdit">
          <v-icon left>mdi-plus</v-icon> Add Currency
        </v-btn>

        <ExportButtons entity="currencies" filename="Currencies" :headers="headers"></ExportButtons>
      </v-card-title>

      <!-- ——— table ——— -->
      <v-data-table :items="filtered" :headers="headers" class="elevation-1">
        <template v-slot:[`item.symbol`]="{ value }">
          {{ value }}
        </template>
        <template v-slot:[`item.decimal_places`]="{ value }">
          {{ value }} decimal{{ value !== 1 ? 's' : '' }}
        </template>
        <template v-slot:[`item.country`]="{ value }">
          {{ findCountryName(value) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          
          <v-tooltip location="top" text="View Details">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
              mdi-eye-outline
            </v-icon>
          </template>
          </v-tooltip>

          <v-tooltip location="top" text="Edit">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" small class="me-1" @click="openDialog(item)" color="secondary" v-if="authStore.isAdmin">
              mdi-pencil
            </v-icon>
          </template>
          </v-tooltip>

          <v-tooltip location="top" text="Delete">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" small @click="deleteItemPrompt(item)" color="error" v-if="authStore.isAdmin">
              mdi-delete
            </v-icon>
          </template>
          </v-tooltip>

        </template>
        <template v-slot:[`no-data`]>
          <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
            {{ tableLoading ? 'Fetching data...' : 'No currencies available or match your search.' }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <!-- ——— dialog ——— -->
    <v-dialog v-model="dialog" max-width="600">
      <v-card>
        <v-card-title>{{ form.id ? 'Edit' : 'Add' }} Currency</v-card-title>
        <v-form ref="currencyForm" v-model="isFormValid" @submit.prevent="save">
        <v-card-text>
          <v-text-field
            v-model="form.currency_code"
            label="Currency Code*"
            maxlength="3"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required, rules.currencyCode]"
            required
            hint="3-letter currency code (e.g., USD, EUR, GBP)"
            persistent-hint
          />

          <v-text-field
            v-model="form.currency_name"
            label="Currency Name*"
            maxlength="255"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required]"
            required
          />

          <v-text-field
            v-model="form.symbol"
            label="Symbol*"
            maxlength="10"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required]"
            required
            hint="Currency symbol (e.g., $, €, £)"
            persistent-hint
          />

          <v-text-field
            v-model="form.decimal_places"
            label="Decimal Places*"
            type="number"
            min="0"
            max="10"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required, rules.decimalPlaces]"
            required
            hint="Number of decimal places (e.g., 2 for $1.00, 0 for ¥100)"
            persistent-hint
          />

          <v-select
            v-model="form.country"
            :items="countries"
            item-title="country_name"
            item-value="country_code"
            label="Country*"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required]"
            required
            hint="Select the country where this currency is used"
            persistent-hint
          />

          <v-textarea
            v-model="form.notes"
            label="Notes (optional)"
            maxlength="1000"
            class="mb-2"
            variant="outlined"
            rows="3"
            hint="Additional information about the currency"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save</v-btn>
        </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
    <v-card :loading="viewDialogLoading">
      <v-card-title class="tableheader white--text d-flex align-center">
        <v-icon left color="white" class="mr-2">mdi-currency-usd</v-icon>
        <span class="text-h5">Currency Details: {{ viewItem?.currency_name }}</span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text v-if="viewItem" class="pt-4">
        <v-list lines="two" density="compact">
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Currency Code:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.currency_code }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>
          
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Currency Name:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.currency_name }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>
          
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Symbol:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.symbol }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>

          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Decimal Places:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.decimal_places }} decimal{{ viewItem.decimal_places !== 1 ? 's' : '' }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>

          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Country:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ findCountryName(viewItem.country) }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>

          <v-list-item v-if="viewItem.notes">
            <template v-slot:prepend>
              <div class="font-weight-medium">Notes:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.notes }}</v-list-item-text>
          </v-list-item><v-divider inset v-if="viewItem.notes"></v-divider>

          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Created:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.created_at) }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>

          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Last Updated:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.updated_at) }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>
        </v-list>
      </v-card-text>
      <v-card-text v-else class="text-center py-5">
          <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
          <p>Loading currency details...</p>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="bg-grey-lighten-4 pa-3">
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card :loading="dialogLoading">
      <v-card-title class="text-h5 error--text">Confirm Deletion</v-card-title>
      <v-card-text>Are you sure you want to delete currency: <strong>{{ itemToDelete?.currency_name }}</strong>?</v-card-text>
      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
          <v-spacer></v-spacer>
      </v-card-actions>
      </v-card>
  </v-dialog>

  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
    {{ snackbar.message }}
    <template v-slot:actions>
      <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
    </template>
  </v-snackbar>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import CurrenciesService from '@/services/CurrenciesService';
import CountriesService from '@/services/CountriesService';
import { useAuthStore } from '@/stores/auth';
import { useSnackbar, formatDateForDisplay } from '@/services/genFunctions';
import ExportButtons from '@/components/ExportButtons.vue';

const authStore = useAuthStore();
/* ——— state ——— */
const currencies = ref([]);
const countries = ref([]);

const viewDialogLoading = ref(false);
const viewDialog = ref(false);
const viewItem = ref(null);
const itemToDelete = ref(null);
const deleteDialog = ref(false);
const dialogLoading = ref(false);
const tableLoading = ref(true);
const currencyForm = ref(null);
const isFormValid = ref(false);

const rules = {
  required: value => !!value || 'This field is required.',
  currencyCode: value => /^[A-Z]{3}$/.test(value || '') || 'Currency code must be exactly 3 uppercase letters.',
  decimalPlaces: value => (value >= 0 && value <= 10 && Number.isInteger(Number(value))) || 'Decimal places must be an integer between 0 and 10.',
};

const { snackbar, showSnackbar } = useSnackbar();

const search = ref('');
const dialog = ref(false);
const form   = ref(resetForm());

const headers = [
  { title: 'Currency Code', key: 'currency_code', width: '150' },
  { title: 'Currency Name', key: 'currency_name', width: '200' },
  { title: 'Symbol', key: 'symbol', width: '100' },
  { title: 'Decimal Places', key: 'decimal_places', width: '150' },
  { title: 'Country', key: 'country', width: '150' },
  { title: 'Actions', key: 'actions', sortable: false, width: '120' }
];

/* ——— lifecycle ——— */

onMounted(async () => {
  tableLoading.value = true;
  try {
    await loadAll();
  } catch (error) {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        showSnackbar(error.response.data.error || 'You are not authorized to view currencies.', 'error');
    } else {
        showSnackbar('Failed to load currencies.', 'error');
    }
    console.error("Error fetching currencies:", error);
    currencies.value = [];
  } finally {
    tableLoading.value = false;
  }
});

async function loadAll() {
  [currencies.value, countries.value] = await Promise.all([
    CurrenciesService.getCurrencies(),
    CountriesService.getCountries()
  ]);
}

/* ——— helpers ——— */
function resetForm() {
  return {
    id: null,
    currency_code: '',
    currency_name: '',
    symbol: '',
    decimal_places: 2,
    country: '',
    notes: ''
  };
}

function findCountryName(countryCode) {
  const country = countries.value.find(c => c.country_code === countryCode);
  return country ? country.country_name : countryCode;
}

const filtered = computed(() => {
  const q = search.value.toLowerCase();
  return currencies.value.filter(c =>
    c.currency_name.toLowerCase().includes(q) ||
    c.currency_code.toLowerCase().includes(q) ||
    c.country.toLowerCase().includes(q)
  );
});

/* ——— crud ——— */
function openDialog(item) {
  form.value = item ? { ...item } : resetForm();
  dialog.value = true;
}

async function save() {
  if (!currencyForm.value) return;
  const { valid } = await currencyForm.value.validate();
  if (!valid) {
    showSnackbar('Please correct the form errors.', 'warning');
    return;
  }
  const { id, ...data } = form.value;
  try {
    if (id) {
      await CurrenciesService.updateCurrency(id, data);
      showSnackbar('Currency updated successfully!', 'success');
    } else {
      await CurrenciesService.createCurrency(data);
      showSnackbar('Currency created successfully!', 'success');
    }
    dialog.value = false;
    await loadAll();
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to save currency.';
    showSnackbar(errorMsg, 'error');
    console.error("Error saving currency:", error);
  }
}

const deleteItemPrompt = (item) => {
  itemToDelete.value = { ...item };
  deleteDialog.value = true;
};

const closeDeleteDialog = () => {
  deleteDialog.value = false;
  nextTick(() => itemToDelete.value = null);
  dialogLoading.value = false;
};

const confirmDelete = async () => {
  if (!itemToDelete.value?.id) return;
  dialogLoading.value = true;
  try {
      await CurrenciesService.deleteCurrency(itemToDelete.value.id);
      currencies.value = currencies.value.filter(c => c.id !== itemToDelete.value.id);
      showSnackbar('Currency deleted successfully!', 'success');
      closeDeleteDialog();
  } catch (error) {
      const errorMsg = error.response?.data?.error || 'Could not delete currency.';
      showSnackbar(errorMsg, 'error', 6000);
      console.error("Error deleting currency:", error.response || error);
  } finally {
      dialogLoading.value = false;
  }
};

const openViewDialog = async (item) => {
  viewDialogLoading.value = true;
  viewDialog.value = true;
  viewItem.value = null; 
  try {
    const fullCurrencyData = await CurrenciesService.getCurrency(item.id);
    viewItem.value = fullCurrencyData;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to load currency details.';
    showSnackbar(errorMsg, 'error');
    console.error("Error fetching currency details for view:", error);
    viewDialog.value = false; 
  } finally {
    viewDialogLoading.value = false;
  }
};

const closeViewDialog = () => {
  viewDialog.value = false;
};
</script>
